// Puzzle-progress storage shared by both ball games. The store is created per
// game with its own localStorage key so the two never overlap. Progress is
// keyed by `boardId` — the content address of the initial board — which is
// stable across puzzle regeneration and re-sorting. The numeric navigation id
// is NOT used as a key: it shifts whenever a set is re-sorted or extended, so
// index-keyed solved records could point at a different board.

export interface PuzzleProgress {
	boardId: string
	movesUsed: number
	solved: boolean
	timestamp: string
}

export function createPuzzleProgressStore(storageKey: string) {
	// Memoized parse: PuzzleHeader calls checkIfSolved on every render, so the
	// whole stored set is re-read and re-parsed per render today. The cache
	// re-parses only when the raw string CHANGES (saveProgress / another tab),
	// so a getItem-per-call stays correct while the JSON.parse cost disappears.
	let cachedRaw: null | string | undefined = undefined
	let cachedProgress: Map<string, PuzzleProgress> = new Map()

	const loadProgress = (): Map<string, PuzzleProgress> => {
		try {
			const data = localStorage.getItem(storageKey)
			if (data === cachedRaw) return cachedProgress
			cachedRaw = data
			if (!data) {
				cachedProgress = new Map()
				return cachedProgress
			}
			const progress: PuzzleProgress[] = JSON.parse(data)
			// Drop legacy entries keyed by the numeric navigation id — they were
			// unreliable by design and cannot be mapped to a board.
			cachedProgress = new Map(
				progress
					.filter(p=> typeof p.boardId === "string")
					.map(p=> [p.boardId, p]),
			)
			return cachedProgress
    } catch {
      // TODO: log
			cachedRaw = null
			cachedProgress = new Map()
			return cachedProgress
		}
	}

	const saveProgress = (boardId: string, movesUsed: number): void=> {
		try {
			const progress = loadProgress()
			progress.set(boardId, {
				boardId,
				movesUsed,
				solved: true,
				timestamp: new Date().toISOString(),
			})
			// Keep the write and the cache in lockstep so the next read is a hit.
			cachedProgress = progress
			const serialized = JSON.stringify(Array.from(progress.values()))
			cachedRaw = serialized
			localStorage.setItem(storageKey, serialized)
		} catch {
			//TODO missing report as we can with miss localstorage or jsonstringify fails
		}
	}

	const checkIfSolved = (boardId: string): boolean=>
		loadProgress().get(boardId)?.solved ?? false

	const getPuzzleProgress = (boardId: string): PuzzleProgress | undefined=>
		loadProgress().get(boardId)

	return { checkIfSolved, getPuzzleProgress, loadProgress, saveProgress }
}
